import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BankStorageKindEnum, MoneyTypeEnum } from 'src/enums';
import { AuthService } from '../login/auth.service';
import { Bank, SearchBankDto } from 'src/typings';

@Injectable({
  providedIn: 'root'
})
export class BankService {
  bankGW: Observable<string> = this.socket.fromEvent<string>('balanceChanges');

  constructor(private http: HttpClient, private authService: AuthService, private socket: Socket) {}

  getList() {
    return this.http
      .get<Bank[]>(`${environment.BACKEND}/bank/list`)
      .pipe(map((rows) => rows.map((row) => this.normalizeBank(row))));
  }

  search(payload?: SearchBankDto) {
    return this.http
      .post<{ data: Bank[]; length: number }>(`${environment.BACKEND}/bank/search`, payload)
      .pipe(
        map((response) => ({
          ...response,
          data: (response.data || []).map((row) => this.normalizeBank(row))
        }))
      );
  }

  create(description: string, moneyType: MoneyTypeEnum) {
    return this.http.post<Bank>(`${environment.BACKEND}/bank/create`, { description, moneyType });
  }

  createWithPayload(payload: {
    description: string;
    moneyType: MoneyTypeEnum;
    storageKind?: BankStorageKindEnum;
    warehouse_id?: number;
    owner_user_id?: number;
    isOperational?: boolean;
  }) {
    return this.http
      .post<Bank>(`${environment.BACKEND}/bank/create`, payload)
      .pipe(map((row) => this.normalizeBank(row)));
  }

  ensureManagerCashboxForUser(userId: number, warehouseId: number) {
    return this.http
      .post<Bank>(`${environment.BACKEND}/user/admin/${userId}/manager-cashbox`, {
        warehouse_id: warehouseId
      })
      .pipe(map((row) => this.normalizeBank(row)));
  }

  update(payload: {
    id: number;
    description: string;
    moneyType: MoneyTypeEnum;
    storageKind?: BankStorageKindEnum;
    warehouse_id?: number | null;
    owner_user_id?: number | null;
    isOperational?: boolean;
  }) {
    return this.http
      .put<Bank>(`${environment.BACKEND}/bank/update`, payload)
      .pipe(map((row) => this.normalizeBank(row)));
  }

  getOne(id: number) {
    return this.http
      .get<Bank>(`${environment.BACKEND}/bank/${id}`)
      .pipe(map((row) => this.normalizeBank(row)));
  }

  private normalizeBank(bank: Bank): Bank {
    return {
      ...bank,
      balance: Number(bank?.balance || 0),
      isCollecting: Boolean(bank?.isCollecting),
      isOperational:
        bank?.isOperational === undefined || bank?.isOperational === null
          ? true
          : Boolean(bank.isOperational),
      warehouse: bank?.warehouse || null,
      ownerUser: bank?.ownerUser || null
    };
  }
}
